import { QuestionViewPropsFor } from '@/components/question/view/types';
import { FillInTheBlankOption } from '@/models/Answer';
import { QuestionTypeEnum } from '@/models/Question';
import { parseFillInTheBlank } from '@/utils/questions/parseFillInTheBlank';
import { hashMap } from '@/utils/shared/hashMap';
import { useMemo } from 'react';

export type FillInTheBlankCorrectionStatus = 'correct' | 'incorrect' | 'empty';

const isCorrect = (
  option: FillInTheBlankOption | undefined,
  position: number,
): FillInTheBlankCorrectionStatus => {
  if (!option) return 'empty';
  const includes = option.validPositions.includes(position);

  return includes ? 'correct' : 'incorrect';
};

export type FillInTheBlankCorrectionSegment =
  | { type: 'text'; value: string }
  | {
      type: 'blank';
      index: number;
      position: number;
      option?: FillInTheBlankOption;
      status: FillInTheBlankCorrectionStatus;
    };

export function useFillInTheBlankCorrection(
  props: QuestionViewPropsFor<QuestionTypeEnum.FILL_IN_THE_BLANK>,
) {
  const { question, value } = props;

  const optionById = useMemo(
    () => hashMap(question.options, option => String(option.id)),
    [question.options],
  );

  const segments = useMemo<FillInTheBlankCorrectionSegment[]>(
    () =>
      parseFillInTheBlank(question.question).map(segment => {
        if (segment.type === 'text') return segment;

        const position = segment.index + 1;
        const optionId = value?.[segment.index];
        const option =
          optionId != null ? optionById.get(String(optionId)) : undefined;
        const status = isCorrect(option, position);

        return { ...segment, position, option, status };
      }),
    [question.question, value, optionById],
  );

  return { segments };
}
